import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Barcode from '../components/Barcode';
import { LabelsModal, UNITS, PRODUCT_TYPES } from './Products';
import { inr, num, dt, catColor, apiError } from '../utils/format';
import { isLoose, packsOf } from '../utils/packs';
import { useAuth } from '../context/AuthContext';
import { useCategories } from '../context/CategoriesContext';
import ReorderLevels from '../components/ReorderLevels';

const TABS = ['Product Details', 'Pricing & Tax', 'Multi Barcode', 'Inventory', 'Reorder Levels', 'Product Ledger'];

const yn = (b) => (b ? 'Yes' : 'No');
const asRows = (v) => { if (Array.isArray(v)) return v; if (typeof v === 'string') { try { return JSON.parse(v) || []; } catch { return []; } } return []; };
const taxLabel = (rate) => (Number(rate) > 0 ? `GST ${Number(rate)}` : '—');
const cessLabel = (rate) => (Number(rate) > 0 ? `${Number(rate)}%` : 'No');

// A two-column attribute table (label → value), matching the reference panels.
const Attrs = ({ rows }) => (
  <table className="attr-table">
    <tbody>
      {rows.map(([label, value]) => (
        <tr key={label}><th>{label}</th><td>{value === '' || value == null ? '' : value}</td></tr>
      ))}
    </tbody>
  </table>
);

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { categories } = useCategories();
  const [data, setData] = useState(null);   // { product, stock, totalQty, ledger, createdByName }
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(TABS[0]);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [labels, setLabels] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/products/${id}`)
      .then(({ data }) => setData(data))
      .catch(() => { toast.error('Product not found'); navigate('/products'); })
      .finally(() => setLoading(false));
  }, [id, navigate]);
  useEffect(load, [load]);

  const onDelete = async () => {
    if (!window.confirm('Deactivate this product?')) return;
    try { await api.delete(`/products/${id}`); toast.success('Product deactivated'); navigate('/products'); }
    catch { toast.error('Could not delete'); }
  };

  if (loading) return <div className="spinner" />;
  if (!data) return null;
  const { product: p, stock, totalQty, ledger, createdByName, ingredients, landingCostPerKg } = data;
  // The till sells a product without packs at its own price; with none set it is dropped silently.
  const noTillPrice = p.isActive !== false && packsOf(p).length === 0 && !(Number(p.sellingPrice) > 0);

  // ── Inline editing ──
  const startEdit = () => { setForm({ ...p, variants: (p.variants || []).filter((v) => v.isSellable !== false).map((v) => ({ ...v })) }); setEdit(true); };
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // Switching to Loose: if there's no per-unit price yet, work it out from a pack
  // (₹180 for a 500 g pack → ₹360 / kg) so the till still has a price to charge.
  const setSellMode = (m) => setForm((f) => {
    const next = { ...f, sellMode: m };
    if (m === 'loose' && !(Number(f.sellingPrice) > 0)) {
      const pack = (f.variants || []).find((v) => Number(v.weightGrams) > 0 && Number(v.sellingPrice) > 0);
      if (pack) {
        const per = (x) => Math.round((Number(x) * 1000 / Number(pack.weightGrams)) * 100) / 100;
        next.sellingPrice = per(pack.sellingPrice); next.mrp = per(pack.mrp || pack.sellingPrice);
      }
    }
    return next;
  });
  const setVar = (i, k, v) => setForm((f) => ({ ...f, variants: f.variants.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)) }));
  const addVar = () => setForm((f) => ({ ...f, variants: [...(f.variants || []), { sku: '', weightLabel: '', weightGrams: 0, sellingPrice: 0, mrp: 0, isSellable: true }] }));
  const rmVar = (i) => setForm((f) => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));
  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        gstRate: Number(form.gstRate) || 0, purchaseGstRate: Number(form.purchaseGstRate) || 0, cessRate: Number(form.cessRate) || 0,
        variants: (form.variants || []).filter((v) => v.weightLabel).map((v) => ({
          ...v, weightGrams: Number(v.weightGrams) || 0, sellingPrice: Number(v.sellingPrice) || 0, mrp: Number(v.mrp) || Number(v.sellingPrice) || 0,
        })),
      };
      await api.put(`/products/${id}`, payload);
      toast.success('Saved'); setEdit(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  // field renderers used in edit mode
  const txt = (k, extra) => <input value={form[k] ?? ''} onChange={(e) => setF(k, e.target.value)} {...extra} />;
  const numf = (k) => <input type="number" step="0.01" style={{ width: 110 }} value={form[k] ?? 0} onChange={(e) => setF(k, e.target.value)} />;
  const chk = (k) => <input type="checkbox" style={{ width: 'auto' }} checked={!!form[k]} onChange={(e) => setF(k, e.target.checked)} />;
  const selF = (k, opts) => <select value={form[k] ?? ''} onChange={(e) => setF(k, e.target.value)}>{opts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}</select>;
  // Category dropdown from the configured list; keeps the product's current value
  // as an option even if it's no longer in the list.
  const catField = () => (
    <select value={form.category ?? ''} onChange={(e) => setF('category', e.target.value)}>
      <option value="">— select category —</option>
      {categories.map((c) => <option key={c} value={c}>{c}</option>)}
      {form.category && !categories.includes(form.category) && <option value={form.category}>{form.category}</option>}
    </select>
  );

  return (
    <div className="prod-detail">
      {/* Header (shown once, above the tabs) */}
      <div className="prod-head">
        <div className="prod-thumb">{(edit ? form.imageUrl : p.imageUrl) ? <img src={edit ? form.imageUrl : p.imageUrl} alt={p.name} /> : <span className="ph">🖼</span>}</div>
        <div className="prod-head-main">
          {edit
            ? <input className="h1-edit" value={form.name || ''} onChange={(e) => setF('name', e.target.value)} placeholder="Product name" />
            : <h1>{p.name}{!p.isActive && <span className="badge badge-gray" style={{ marginLeft: '.6rem' }}>Inactive</span>}</h1>}
          <table className="attr-table compact">
            <tbody>
              <tr><th>Short Description</th><td>{edit ? txt('shortDescription', { style: { width: '100%' } }) : (p.shortDescription || '')}</td></tr>
              <tr><th>Description</th><td>{edit ? <textarea rows={2} style={{ width: '100%' }} value={form.description || ''} onChange={(e) => setF('description', e.target.value)} /> : (p.description || '')}</td></tr>
              {edit && <tr><th>Image URL</th><td>{txt('imageUrl', { style: { width: '100%' }, placeholder: '/images/…' })}</td></tr>}
              {edit && <tr><th>Active</th><td>{chk('isActive')}</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="tabs-actions" style={{ alignSelf: 'flex-start' }}>
          {edit ? (
            <>
              <button className="btn btn-primary btn-sm" onClick={saveEdit} disabled={saving}>{saving ? 'Saving…' : '✓ Save'}</button>
              <button className="btn btn-outline btn-sm" onClick={() => setEdit(false)} disabled={saving}>Cancel</button>
            </>
          ) : (
            <>
              <button className="btn btn-outline btn-sm" onClick={() => setLabels(true)} title="Print barcode labels">▤ Labels</button>
              {can('products:write') && <button className="btn btn-primary btn-sm" onClick={startEdit} title="Edit product, tax & variants inline">✎ Edit</button>}
              {can('products:write') && <button className="btn btn-danger btn-sm" onClick={onDelete} title="Deactivate">🗑</button>}
            </>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="tabs">
        {TABS.filter((t) => t !== 'Reorder Levels' || can('thresholds:read')).map((t) => (
          <button key={t} className={`tab${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {tab === 'Product Details' && (
        <div className="prod-cols">
          <div className="card card-pad">
            <div className="attr-two">
              <Attrs rows={edit ? [
                ['Product Type', selF('productType', [...new Set([...PRODUCT_TYPES, form.productType].filter(Boolean))].map((t) => ({ v: t, l: t })))],
                ['Source', selF('source', [{ v: 'made', l: 'Made in-house (Kitchen)' }, { v: 'bought', l: 'Bought (Vendor)' }])],
                ['Sold as', <select value={form.sellMode || 'packs'} onChange={(e) => setSellMode(e.target.value)}><option value="packs">Packs</option><option value="loose">Loose (by {form.unit || 'kg'})</option></select>],
                ['Print Name', txt('printName')],
                ['Category', catField()],
                ['Sub Category', txt('subCategory')],
                ['Department', txt('department')],
              ] : [
                ['Product Type', p.productType],
                ['Source', p.source === 'bought' ? 'Bought (Vendor)' : 'Made in-house (Kitchen)'],
                ['Sold as', isLoose(p) ? `Loose (by ${p.unit || 'kg'})` : 'Packs'],
                ['Print Name', p.printName || p.name],
                ['Category', <span className="cat-chip" style={{ '--cat': catColor(p.category) }}>{p.category}</span>],
                ['Sub Category', p.subCategory],
                ['Department', p.department],
              ]} />
              <Attrs rows={edit ? [
                ['Brand', txt('brand')],
                ['Sub Brand', txt('subBrand')],
                ['Unit', selF('unit', [...new Set([...UNITS, form.unit].filter(Boolean))].map((u) => ({ v: u, l: u })))],
                ...(form.sellMode === 'loose' ? [] : [['Net Weight Unit', txt('netWeightUnit')]]),
                ['Shelf Life', txt('shelfLife')],
                ['Multiple Batch', chk('multipleBatch')],
              ] : [
                ['Brand', p.brand],
                ['Sub Brand', p.subBrand],
                ['Unit', p.unit],
                ...(isLoose(p) ? [] : [['Net Weight Unit', p.netWeightUnit]]),   // loose items have no fixed net weight
                ['Shelf Life', p.shelfLife],
                ['Multiple Batch', yn(p.multipleBatch)],
              ]} />
            </div>
            {edit ? (
              <div style={{ marginTop: '.6rem' }}>
                <div className="side-card-title">Additional Info</div>
                <textarea rows={2} style={{ width: '100%' }} value={form.additionalInfo || ''} onChange={(e) => setF('additionalInfo', e.target.value)} />
              </div>
            ) : p.additionalInfo && (
              <div style={{ marginTop: '.6rem' }}>
                <div className="side-card-title">Additional Info</div>
                <p className="muted" style={{ margin: '.2rem 0 0' }}>{p.additionalInfo}</p>
              </div>
            )}
            <div className="muted small" style={{ marginTop: '.8rem' }}>Created {dt(p.createdAt)}{createdByName ? ` by ${createdByName}` : ''}</div>
          </div>

          <div className="prod-col-side">
            <div className="card card-pad total-qty">
              <div className="tq-label">Total Qty</div>
              <div className="tq-value">{num(totalQty)} {p.unit}</div>
            </div>
            {ingredients && ingredients.length > 0 && (
              <div className="card card-pad">
                <div className="side-card-title">Ingredients <Link to={`/recipes/${ingredients[0].recipeId}`} className="muted small" style={{ fontWeight: 400 }} title={ingredients[0].recipe}>· view recipe</Link></div>
                <table className="attr-table compact">
                  <tbody>
                    {ingredients.map((ing, i) => <tr key={i}><th>{ing.name}</th><td>{num(ing.quantity)} {ing.unit}</td></tr>)}
                  </tbody>
                </table>
                <p className="muted small" style={{ marginTop: '.4rem' }}>Per {num(ingredients[0].baseYieldQty)} {ingredients[0].baseYieldUnit} · set in Recipes / BOM</p>
              </div>
            )}
            <div className="card card-pad">
              <div className="side-card-title">Nutrition</div>
              <table className="attr-table compact">
                <tbody>
                  {asRows(p.nutrition).length === 0
                    ? <tr><td className="muted center">Not available</td></tr>
                    : asRows(p.nutrition).map((n, i) => <tr key={i}><th>{n.name}</th><td>{n.value}</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'Product Ledger' && (
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>When</th><th>Type</th><th>Location</th><th>Variant</th><th className="right">Qty</th><th>Ref</th><th>Note</th></tr></thead>
            <tbody>
              {ledger.map((e) => (
                <tr key={e.id}>
                  <td>{dt(e.createdAt)}</td>
                  <td><span className="badge badge-gray">{e.txnType}</span></td>
                  <td>{e.location}</td>
                  <td>{e.variant || '—'}</td>
                  <td className={`right ${e.quantity < 0 ? 'neg' : 'pos'}`}>{e.quantity > 0 ? '+' : ''}{num(e.quantity)}</td>
                  <td>{e.refType ? `${e.refType}#${e.refId ?? ''}` : '—'}</td>
                  <td>{e.note || '—'}</td>
                </tr>
              ))}
              {/* Oldest event: product creation */}
              <tr className="muted"><td>{dt(p.createdAt)}</td><td><span className="badge badge-blue">created</span></td><td colSpan={5}>Product created{createdByName ? ` by ${createdByName}` : ''}</td></tr>
            </tbody>
          </table>
        </div>
      )}

      {tab === 'Pricing & Tax' && (
        <>
          {noTillPrice && <p className="form-error" style={{ marginTop: 0 }}>No price per {p.unit || 'unit'} — this item won't appear at the till. Click Edit and set a selling price.</p>}
          {/* Packs / loose price */}
          {edit ? (
            <>
              {form.sellMode === 'loose' ? (
                <p className="muted small" style={{ marginTop: 0 }}>Sold loose by {form.unit || 'kg'} — no packs. Saving takes any packs off sale (their history is kept). Change <strong>Sold as</strong> on Product Details to sell in packs.</p>
              ) : (<>
              <div className="section-head" style={{ marginTop: 0 }}><span className="muted small">Packs — leave empty for by-weight items (kg/L)</span><button className="btn btn-outline btn-sm" onClick={addVar}>+ Add pack</button></div>
              <div className="table-wrap">
                <table className="grid">
                  <thead><tr><th>SKU</th><th>Pack label *</th><th className="right">Grams</th><th className="right">Selling ₹ (incl GST)</th><th className="right">MRP</th><th className="right">Purchase ₹ <span className="muted small">(bought)</span></th><th title="Off sale hides the pack from the till and from labels, keeping its history">Sellable</th><th></th></tr></thead>
                  <tbody>
                    {(form.variants || []).length === 0 && <tr><td colSpan={8} className="empty">No packs — sold by weight (kg/L).</td></tr>}
                    {(form.variants || []).map((v, i) => (
                      <tr key={i}>
                        <td><input value={v.sku || ''} onChange={(e) => setVar(i, 'sku', e.target.value)} placeholder="PICK-…" /></td>
                        <td><input value={v.weightLabel} onChange={(e) => setVar(i, 'weightLabel', e.target.value)} placeholder="500g" /></td>
                        <td className="right"><input type="number" style={{ width: 80 }} value={v.weightGrams} onChange={(e) => setVar(i, 'weightGrams', e.target.value)} /></td>
                        <td className="right"><input type="number" step="0.01" style={{ width: 100 }} value={v.sellingPrice} onChange={(e) => setVar(i, 'sellingPrice', e.target.value)} /></td>
                        <td className="right"><input type="number" step="0.01" style={{ width: 100 }} value={v.mrp} onChange={(e) => setVar(i, 'mrp', e.target.value)} /></td>
                        <td className="right"><input type="number" step="0.01" style={{ width: 90 }} value={v.purchasePrice || 0} onChange={(e) => setVar(i, 'purchasePrice', e.target.value)} /></td>
                        <td><input type="checkbox" checked={v.isSellable !== false} onChange={(e) => setVar(i, 'isSellable', e.target.checked)} /></td>
                        <td><button className="btn btn-danger btn-sm" onClick={() => rmVar(i)}>✕</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(p.variants || []).filter((v) => v.isSellable === false && !(form.variants || []).some((f) => f.id === v.id)).length > 0 && (
                <p className="muted small" style={{ marginTop: '.5rem' }}>
                  Off sale — kept because stock has moved against them:{' '}
                  {(p.variants || []).filter((v) => v.isSellable === false && !(form.variants || []).some((f) => f.id === v.id)).map((v) => (
                    <button key={v.id} type="button" className="btn btn-outline btn-sm" style={{ marginRight: '.4rem' }}
                      onClick={() => setForm((f) => ({ ...f, variants: [...(f.variants || []), { ...v, isSellable: true }] }))}>
                      {v.weightLabel} — restore
                    </button>
                  ))}
                </p>
              )}
              </>)}
              {((form.variants || []).length === 0 || form.sellMode === 'loose') && (
                <div className="row" style={{ maxWidth: 640, marginTop: '.6rem' }}>
                  <div className="field"><label>Selling price ₹ <span className="muted small">(per {form.unit || 'unit'}, incl GST)</span></label>{numf('sellingPrice')}</div>
                  <div className="field"><label>MRP ₹</label>{numf('mrp')}</div>
                  {form.source === 'bought' && <div className="field"><label>Purchase ₹ <span className="muted small">(vendor cost)</span></label>{numf('purchasePrice')}</div>}
                </div>
              )}
              {p.source !== 'bought' && landingCostPerKg != null && <p className="muted small" style={{ marginTop: '.6rem' }}>Landing cost (from recipe / BOM): <strong>{inr(landingCostPerKg)} / kg</strong> — set in Recipes / BOM.</p>}
            </>
          ) : !isLoose(p) && (p.variants || []).length > 0 ? (
            <>
              <div className="table-wrap">
                <table className="grid">
                  <thead><tr><th>SKU</th><th>Pack</th><th className="right">Grams</th><th className="right">MRP</th><th className="right">Selling (incl GST)</th>{p.source === 'bought' && <th className="right">Purchase</th>}<th>Sellable</th></tr></thead>
                  <tbody>
                    {p.variants.map((v) => (
                      <tr key={v.id}>
                        <td>{v.sku || '—'}</td><td>{v.weightLabel}</td><td className="right">{v.weightGrams || '—'}</td>
                        <td className="right">{inr(v.mrp)}</td><td className="right">{inr(v.sellingPrice)}</td>
                        {p.source === 'bought' && <td className="right">{inr(v.purchasePrice)}</td>}
                        <td>{v.isSellable ? <span className="badge badge-green">Yes</span> : <span className="badge badge-gray">No</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {p.source !== 'bought' && landingCostPerKg != null && <p className="muted small mt">Landing cost (from recipe / BOM): <strong>{inr(landingCostPerKg)} / kg</strong>.</p>}
            </>
          ) : (
            <div className="card card-pad" style={{ maxWidth: 440 }}>
              <div className="side-card-title">Price (by weight)</div>
              <Attrs rows={[
                ['Selling price', `${inr(p.sellingPrice)} / ${p.unit}`], ['MRP', inr(p.mrp)],
                ...(p.source === 'bought' ? [['Purchase (vendor)', `${inr(p.purchasePrice)} / ${p.unit}`]] : []),
                ...(p.source !== 'bought' && landingCostPerKg != null ? [['Landing cost (recipe)', `${inr(landingCostPerKg)} / kg`]] : []),
              ]} />
            </div>
          )}

          {/* Tax */}
          <div className="attr-grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: '1rem' }}>
            <div className="card card-pad">
              <div className="side-card-title">Sales Tax</div>
              <Attrs rows={edit ? [
                ['Sales GST %', numf('gstRate')], ['Sales Tax Inclusive (MRP)', chk('salesTaxInclusive')], ['Cess %', numf('cessRate')], ['HSN Code', txt('hsnCode')],
              ] : [
                ['Sales GST', taxLabel(p.gstRate)], ['Sales Tax Inclusive (MRP)', yn(p.salesTaxInclusive)], ['Cess', cessLabel(p.cessRate)], ['HSN Code', p.hsnCode],
              ]} />
            </div>
            <div className="card card-pad">
              <div className="side-card-title">Purchase Tax</div>
              <Attrs rows={edit ? [
                ['Purchase GST %', numf('purchaseGstRate')], ['Purchase Tax Inclusive', chk('purchaseTaxInclusive')],
              ] : [
                ['Purchase GST', taxLabel(p.purchaseGstRate)], ['Purchase Tax Inclusive', yn(p.purchaseTaxInclusive)],
              ]} />
            </div>
          </div>
        </>
      )}

      {tab === 'Multi Barcode' && (
        <div className="barcode-grid">
          <div className="card card-pad center">
            <div className="muted">Product</div>
            <div style={{ fontWeight: 700 }}>{p.name}</div>
            <Barcode value={p.barcode} />
            <div className="muted small">{p.qrCode}</div>
          </div>
          {(p.variants || []).map((v) => (
            <div key={v.id} className="card card-pad center">
              <div className="muted">{v.weightLabel} · {inr(v.sellingPrice)}</div>
              <Barcode value={v.barcode} />
              <div className="muted small">{v.sku || v.qrCode}</div>
            </div>
          ))}
        </div>
      )}

      {tab === 'Inventory' && (
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Location</th><th>Variant</th><th className="right">Quantity</th><th>Unit</th></tr></thead>
            <tbody>
              {stock.map((s, i) => (
                <tr key={i}><td>{s.locationName}</td><td>{s.variantLabel}</td><td className="right">{num(s.quantity)}</td><td>{s.unit}</td></tr>
              ))}
              {stock.length === 0 && <tr><td colSpan={4} className="empty">No stock on hand.</td></tr>}
              {stock.length > 0 && (
                <tr className="total-row"><td colSpan={2}><strong>Total</strong></td><td className="right"><strong>{num(totalQty)}</strong></td><td>{p.unit}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'Reorder Levels' && <ReorderLevels product={p} canWrite={can('thresholds:write')} />}

      <div style={{ marginTop: '1rem' }}><Link to="/products" className="btn btn-outline btn-sm">← Back to Products</Link></div>

      {labels && <LabelsModal product={p} onClose={() => setLabels(false)} />}
    </div>
  );
};

export default ProductDetail;
